/**
 * PublicLoadingSkeleton — shimmer placeholder while public data loads.
 * Accepts a `viewType` prop to display a shape-matching skeleton.
 */

import React from 'react';

const shimmerCss = `
  @keyframes publicShimmer {
    0%   { background-position: -800px 0; }
    100% { background-position:  800px 0; }
  }
`;

const shimmerStyle = {
  borderRadius: 8,
  background: 'linear-gradient(90deg, #f1f5f9 25%, #e8eef5 50%, #f1f5f9 75%)',
  backgroundSize: '800px 100%',
  animation: 'publicShimmer 1.6s ease-in-out infinite',
};

function Box({ height = 20, width = '100%', style = {}, className = '' }) {
  return (
    <div
      className={className}
      style={{ height, width, ...shimmerStyle, borderRadius: 'var(--card-radius)', ...style }}
    />
  );
}

/* ── Individual skeleton shapes per view type ── */

function CardSkeleton() {
  return (
    <div className="row g-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="col-12 col-md-6 col-lg-4">
          <div
            style={{
              borderRadius: 'var(--card-radius)',
              border: '1px solid #f1f5f9',
              boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <Box height={200} style={{ borderRadius: 0 }} />
            <div style={{ padding: '1.1rem 1.3rem' }}>
              <Box height={20} width="75%" className="mb-2" />
              <Box height={13} width="50%" className="mb-2" />
              <Box height={12} width="92%" className="mb-1" />
              <Box height={12} width="70%" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          style={{
            display: 'flex', alignItems: 'center', gap: '1rem',
            padding: '1rem 1.3rem',
            borderRadius: 'var(--card-radius)',
            border: '1px solid #f1f5f9',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
          }}
        >
          <Box height={88} width={88} style={{ borderRadius: 14, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Box height={18} width="65%" className="mb-2" />
            <Box height={13} width="40%" className="mb-2" />
            <Box height={12} width="84%" />
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <Box height={22} width={64} style={{ borderRadius: 999 }} />
            <Box height={28} width={28} style={{ borderRadius: '50%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div
      style={{
        borderRadius: 'var(--card-radius)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex', gap: 0,
          padding: '0.75rem 1rem',
          borderBottom: '2px solid #f1f5f9',
          background: '#f8fafc',
        }}
      >
        {[15, 18, 12, 20, 14].map((w, i) => (
          <Box key={i} height={14} width={`${w}%`} style={{ margin: '0 1rem 0 0' }} />
        ))}
      </div>
      {/* Data rows */}
      {[1, 2, 3, 4, 5, 6].map((r, ri) => (
        <div
          key={r}
          style={{
            display: 'flex', gap: 0,
            padding: '0.85rem 1rem',
            borderBottom: ri < 5 ? '1px solid #f8fafc' : 'none',
            background: ri % 2 === 1 ? '#fafbfd' : '#fff',
          }}
        >
          {[15, 18, 12, 20, 14].map((w, i) => (
            <Box key={i} height={13} width={`${w}%`} style={{ margin: '0 1rem 0 0' }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div style={{ position: 'relative', paddingLeft: 40 }}>
      {/* Rail */}
      <div
        style={{
          position: 'absolute', left: 12, top: 10, bottom: 10, width: 3,
          background: 'linear-gradient(to bottom, #e2e8f0, #f1f5f9)',
          borderRadius: 3,
        }}
      />
      {[1, 2, 3, 4].map((i, idx) => (
        <div
          key={i}
          style={{
            position: 'relative',
            marginBottom: idx < 3 ? 24 : 0,
          }}
        >
          {/* Dot */}
          <div
            style={{
              position: 'absolute', left: -32, top: 16,
              width: 20, height: 20, borderRadius: '50%',
              background: '#e2e8f0',
            }}
          />
          {/* Card */}
          <div
            style={{
              background: '#fff',
              borderRadius: 'var(--card-radius)',
              border: '1px solid #f1f5f9',
              padding: '1rem 1.25rem',
              boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
            }}
          >
            <Box height={22} width={110} style={{ borderRadius: 999, marginBottom: 12 }} />
            <Box height={18} width="60%" className="mb-2" />
            <Box height={13} width="40%" className="mb-1" />
            <Box height={12} width="84%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PublicLoadingSkeleton({ viewType = 'card' }) {
  return (
    <div className="container py-4">
      <style>{shimmerCss}</style>

      {/* Header skeleton */}
      <div
        style={{
          borderRadius: 'var(--card-radius)', border: '1px solid #f1f5f9', background: '#fff',
          padding: '2rem 2rem 1.8rem', marginBottom: '2rem',
          boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
        }}
      >
        <Box height={36} width="42%" className="mb-3" />
        <Box height={16} width="62%" className="mb-1" />
        <Box height={14} width="50%" />
      </div>

      {/* View-type specific skeleton */}
      {viewType === 'list'     && <ListSkeleton />}
      {viewType === 'table'    && <TableSkeleton />}
      {viewType === 'timeline' && <TimelineSkeleton />}
      {(viewType === 'card' || !viewType) && <CardSkeleton />}
    </div>
  );
}
